import { ReactNode } from 'react';
import { cn } from '@/shared/lib/utils';
import { Loader2, AlertCircle, Inbox, RefreshCw } from 'lucide-react';
import { Button } from '@/shared/ui/button';
import { ContentCard } from './content-cards';

// ローディング状態
interface LoadingStateProps {
  message?: string;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
}

export const LoadingState: React.FC<LoadingStateProps> = ({
  message = '読み込み中...',
  className,
  size = 'md'
}) => {
  const sizeClasses = {
    sm: 'py-8',
    md: 'py-12',
    lg: 'py-16'
  };

  const iconSizes = {
    sm: 'h-6 w-6',
    md: 'h-8 w-8',
    lg: 'h-10 w-10'
  };

  return (
    <div className={cn(
      'flex flex-col items-center justify-center text-center',
      sizeClasses[size],
      className
    )}>
      <Loader2 className={cn('animate-spin text-gray-400 mb-4', iconSizes[size])} />
      <p className="text-sm text-gray-600 dark:text-gray-400">{message}</p>
    </div>
  );
};

// エラー状態
interface ErrorStateProps {
  title?: string;
  message?: string;
  onRetry?: () => void;
  retryLabel?: string;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
}

export const ErrorState: React.FC<ErrorStateProps> = ({
  title = 'エラーが発生しました',
  message = '問題が発生しました。しばらく時間をおいて再度お試しください。',
  onRetry,
  retryLabel = '再試行',
  className,
  size = 'md'
}) => {
  const sizeClasses = {
    sm: 'py-8',
    md: 'py-12',
    lg: 'py-16'
  };

  const iconSizes = {
    sm: 'h-6 w-6',
    md: 'h-8 w-8',
    lg: 'h-10 w-10'
  };

  return (
    <ContentCard className={cn(sizeClasses[size], className)}>
      <div className="flex flex-col items-center justify-center text-center">
        <AlertCircle className={cn('text-red-500 mb-4', iconSizes[size])} />
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
          {title}
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-6 max-w-md">
          {message}
        </p>
        {onRetry && (
          <Button onClick={onRetry} variant="outline" className="flex items-center space-x-2">
            <RefreshCw className="h-4 w-4" />
            <span>{retryLabel}</span>
          </Button>
        )}
      </div>
    </ContentCard>
  );
};

// 空状態
interface EmptyStateProps {
  title?: string;
  message?: string;
  action?: {
    label: string;
    onClick: () => void;
  };
  icon?: ReactNode;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
}

export const EmptyState: React.FC<EmptyStateProps> = ({
  title = 'データがありません',
  message = 'まだデータが登録されていません。',
  action,
  icon,
  className,
  size = 'md'
}) => {
  const sizeClasses = {
    sm: 'py-8',
    md: 'py-12', 
    lg: 'py-16'
  };

  const iconSizes = {
    sm: 'h-12 w-12',
    md: 'h-16 w-16',
    lg: 'h-20 w-20'
  };

  return (
    <ContentCard className={cn(sizeClasses[size], className)}>
      <div className="flex flex-col items-center justify-center text-center">
        <div className={cn('text-gray-300 dark:text-gray-600 mb-4', iconSizes[size])}>
          {icon || <Inbox className={iconSizes[size]} />}
        </div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
          {title}
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-6 max-w-md">
          {message}
        </p>
        {action && (
          <Button onClick={action.onClick} className="flex items-center space-x-2">
            <span>{action.label}</span>
          </Button>
        )}
      </div>
    </ContentCard>
  );
};

// 検索結果なし状態（EmptyStateの特殊版）
interface NoSearchResultsProps extends Omit<EmptyStateProps, 'title' | 'message'> {
  searchTerm?: string;
  onClearSearch?: () => void;
}

export const NoSearchResults: React.FC<NoSearchResultsProps> = ({
  searchTerm = '',
  onClearSearch,
  ...props
}) => {
  return (
    <EmptyState
      title="検索結果が見つかりません"
      message={
        searchTerm 
          ? `「${searchTerm}」に一致する結果が見つかりませんでした。`
          : '検索条件に一致する結果が見つかりませんでした。'
      }
      action={
        onClearSearch 
          ? {
              label: '検索をクリア',
              onClick: onClearSearch
            }
          : undefined
      }
      {...props}
    />
  );
};